import React, { useEffect, useState } from 'react';
import { Colors } from 'library';
import { Card, ResponsiveRow, Row, Stack, Text, Title } from 'library/ui';
import { styled } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import theme from 'theme';
import { client, consoleLogInDev, track } from 'shared';
import { HomeFindSection } from './HomeFindSection';
import { StaffNearYou } from './StaffNearYou';
import { useHistory } from 'react-router';
import useQueryParams from 'shared/QueryParamsHook';
import { ITeacher } from 'models/Providers/Teacher';
import { StaffFilterOptions, defaultFilters } from 'parent-portal/shared/filterOptions';

interface SpotlightWorkersResponse {
    workers: ITeacher[];
    vetted_workers_count: number;
}

function TrustItem({ text }: { text: string }) {
    return (
        <Row gap="sm">
            <CheckCircle style={{ color: Colors.secondary, fontSize: 22 }} />
            <Text size="sm">{text}</Text>
        </Row>
    );
}

export function HomeHeader({ signUp }: { signUp: () => void }) {
    const history = useHistory();
    const query = useQueryParams();
    const searchedZipCode = query.get('zip');
    const queryFilters = query.get('filters');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [zipCode, setZipCode] = useState<string>(query.get('zip') || '');
    const [spotlightWorkers, setSpotlightWorkers] = useState<ITeacher[]>([]);
    const [vettedWorkersCount, setVettedWorkersCount] = useState<number>(100);
    const [filters, setFilters] = useState<StaffFilterOptions>(
        queryFilters ? JSON.parse(atob(queryFilters)) : defaultFilters,
    );

    useEffect(() => {
        if (!searchedZipCode) {
            return;
        }
        setIsLoading(true);
        setSpotlightWorkers([]);
        setVettedWorkersCount(100);
        client(
            `api/contact-info-location/spotlighted-workers-by-location/?zip_code=${zipCode}${getEncodedFiltersQueryString(filters)}`,
        )
            .then((response: SpotlightWorkersResponse) => {
                setSpotlightWorkers(response.workers);
                setVettedWorkersCount(response.vetted_workers_count);
            })
            .catch(consoleLogInDev)
            .finally(() => {
                setIsLoading(false);
            });
    }, [searchedZipCode, queryFilters]);

    function updateZipCode(value: string) {
        if (value.length <= 5 && !isNaN(Number(value))) {
            setZipCode(value.trim());
            setFilters(defaultFilters);
        }
    }

    function getEncodedFiltersQueryString(filters: StaffFilterOptions) {
        return filters.education || filters.experience ? `&filters=${btoa(JSON.stringify(filters))}` : '';
    }

    function onSearch(newFilters: StaffFilterOptions) {
        setFilters(newFilters);
        const url = `/worker-search?zip=${zipCode}${getEncodedFiltersQueryString(newFilters)}`;
        history.push(url);
        track('Customer Searched for Staff Near Them');
    }

    if (searchedZipCode) {
        return (
            <StaffNearYou
                signUp={signUp}
                zipCode={zipCode}
                searchedZipCode={searchedZipCode}
                updateZipCode={updateZipCode}
                onSearch={onSearch}
                loading={isLoading}
                workers={spotlightWorkers}
                vettedWorkersCount={vettedWorkersCount}
                filters={filters}
            />
        );
    }

    return (
        <HeroBand align="center" justify="center">
            <Stack maxWidth="lg" fullWidth>
                <ResponsiveRow collapseAt="md" align="center" justify="between" gap="lg">
                    <Stack gap="lg" grow>
                        <Text size="xs" weight="bold" tone="accent" uppercase>
                            The #1 staffing platform for child care
                        </Text>
                        <HeroTitle size="h1">Staff your child care center in minutes.</HeroTitle>
                        <Stack maxWidth="sm">
                            <Text tone="secondary">
                                Never scramble for child care staff again. Quickly book vetted, qualified substitutes
                                through Tandem, the trusted platform for child care staffing.
                            </Text>
                        </Stack>
                        <Stack gap="sm">
                            <TrustItem text="Fully compliant, state-licensed staff" />
                            <TrustItem text="Verified child care credentials" />
                            <TrustItem text="Flexible short-term or full-time support" />
                        </Stack>
                    </Stack>
                    <SearchCard padding="lg">
                        <HomeFindSection zipCode={zipCode} updateZipCode={updateZipCode} onSearch={onSearch} />
                    </SearchCard>
                </ResponsiveRow>
            </Stack>
        </HeroBand>
    );
}

const HeroBand = styled(Stack)({
    backgroundColor: Colors.white,
    minHeight: '85vh',
    padding: '48px 80px',
    [theme.breakpoints.down('md')]: {
        padding: '48px 20px',
    },
});

const HeroTitle = styled(Title)({
    fontSize: 48,
    lineHeight: 1.1,
});

const SearchCard = styled(Card)({
    width: 560,
    maxWidth: '100%',
    flexShrink: 0,
    border: 'none',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
});
